import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingCart, Star, ArrowLeft, Plus, Minus, Check, Shield, Trophy, Target } from 'lucide-react';
import { productService } from '../services/api';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await productService.getById(id);
      setProduct(response.data);
      if (response.data.images?.length > 0) {
        setSelectedImage(0);
      }
    } catch (error) {
      toast.error('Error al cargar el producto');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!selectedSize || !selectedColor) {
      toast.error('Por favor selecciona talla y color');
      return;
    }

    const availableStock = product.stock[selectedSize]?.[selectedColor] || 0;
    if (availableStock < quantity) {
      toast.error('No hay suficiente stock disponible');
      return;
    }

    addItem(product, selectedSize, selectedColor, quantity);
    toast.success('Producto agregado al carrito');
  };

  const getAvailableStock = () => {
    if (!selectedSize || !selectedColor) return 0;
    return product.stock[selectedSize]?.[selectedColor] || 0;
  };

  const getDiscountedPrice = () => {
    if (product.discount_percentage > 0) {
      return product.price * (1 - product.discount_percentage / 100);
    }
    return product.price;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="bg-gray-300 h-96 rounded-lg animate-pulse"></div>
            <div className="flex space-x-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-300 h-20 w-20 rounded-lg animate-pulse"></div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-gray-300 h-8 rounded animate-pulse"></div>
            <div className="bg-gray-300 h-6 rounded animate-pulse"></div>
            <div className="bg-gray-300 h-12 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Producto no encontrado</h2>
        <button
          onClick={() => navigate('/')}
          className="btn-primary"
        >
          Volver a la tienda
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Volver a la tienda</span>
      </motion.button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Product Images */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-4"
        >
          {/* Main Image */}
          <div className="relative">
            <img
              src={product.images?.[selectedImage] || '/api/placeholder/500/500'}
              alt={product.name}
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />
            
            {/* Discount Badge */}
            {product.discount_percentage > 0 && (
              <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                -{product.discount_percentage}%
              </div>
            )}

            {/* Featured Badge */}
            {product.is_featured && (
              <div className="absolute top-4 right-4 bg-yellow-500 text-white p-2 rounded-full">
                <Star className="w-5 h-5" />
              </div>
            )}

            {/* Official Badge */}
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold text-red-600 flex items-center space-x-1">
              <Shield className="w-4 h-4" />
              <span>Oficial</span>
            </div>
          </div>

          {/* Thumbnail Images */}
          {product.images?.length > 1 && (
            <div className="flex space-x-2 overflow-x-auto">
              {product.images.map((image, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                    selectedImage === index ? 'border-red-500' : 'border-gray-200'
                  }`}
                >
                  <img
                    src={image}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </motion.button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Product Info */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-6"
        >
          <div>
            <span className="text-sm text-red-600 font-medium bg-red-50 px-3 py-1 rounded-full">
              {product.category}
            </span>
            <h1 className="text-3xl font-bold text-gray-800 mt-3">{product.name}</h1>
            <div className="flex items-center space-x-2 mt-2">
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                ))}
              </div>
              <span className="text-sm text-gray-600">(4.5) • 128 reseñas</span>
            </div>
          </div>

          {/* Club Badge */}
          <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-lg border border-red-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-800 rounded-full flex items-center justify-center">
                <Shield className="text-white w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-red-700">Club Romerense</h3>
                <p className="text-sm text-red-600">Producto Oficial</p>
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="flex items-center space-x-3">
            <span className="text-3xl font-bold text-red-600">
              ${getDiscountedPrice().toFixed(2)}
            </span>
            {product.discount_percentage > 0 && (
              <span className="text-xl text-gray-500 line-through">
                ${product.price}
              </span>
            )}
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Descripcion</h3>
            <p className="text-gray-600 leading-relaxed">{product.long_description}</p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Target className="w-6 h-6 text-red-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700">Calidad Premium</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Shield className="w-6 h-6 text-red-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700">Producto Oficial</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Trophy className="w-6 h-6 text-red-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700">Pasión Romerense</p>
            </div>
          </div>

          {/* Size Selection */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Talla</h3>
            <div className="flex flex-wrap gap-2">
              {product.sizes?.map((size) => (
                <motion.button
                  key={size}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedSize(size)}
                  className={`px-4 py-2 rounded-lg border-2 font-medium transition-colors ${
                    selectedSize === size
                      ? 'border-red-500 bg-red-50 text-red-600'
                      : 'border-gray-200 text-gray-700 hover:border-red-300'
                  }`}
                >
                  {size}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Color Selection */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Color</h3>
            <div className="flex flex-wrap gap-2">
              {product.colors?.map((color) => (
                <motion.button
                  key={color}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedColor(color)}
                  className={`px-4 py-2 rounded-lg border-2 font-medium transition-colors ${
                    selectedColor === color
                      ? 'border-red-500 bg-red-50 text-red-600'
                      : 'border-gray-200 text-gray-700 hover:border-red-300'
                  }`}
                >
                  {color}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Stock Info */}
          {selectedSize && selectedColor && (
            <div className="flex items-center space-x-2 text-sm bg-green-50 p-3 rounded-lg">
              <Check className="w-4 h-4 text-green-500" />
              <span className="text-gray-600">
                Stock disponible: {getAvailableStock()} unidades
              </span>
            </div>
          )}

          {/* Quantity */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Cantidad</h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-2 rounded-full bg-gray-100 hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </motion.button>
                <span className="w-12 text-center font-semibold">{quantity}</span>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-2 rounded-full bg-gray-100 hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAddToCart}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-800 text-white py-3 px-6 rounded-lg font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <ShoppingCart className="w-5 h-5" />
              <span>Agregar al Carrito</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-3 rounded-lg border-2 border-red-200 hover:border-red-300 hover:bg-red-50 transition-colors"
            >
              <Heart className="w-6 h-6 text-gray-600 hover:text-red-600" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductDetail;
